import { ArrowRightLeft, LanguagesIcon, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Language,
  SupportedLanguages,
  getLanguageByKey,
} from "@/components/language/supported-languages";
import React, { useEffect, useRef, useState } from "react";
import { Role, VoiceChatMessage, pcmBase64ToWavUrl } from "@/lib/messaging";

// Convert base64audio → stable blob URL once so re-renders don't recreate it
const normalizeResponses = (responses: TranslationResponse[]): TranslationResponse[] =>
  responses.map((r) => ({
    text: r.text,
    src: r.src ?? (r.base64audio ? pcmBase64ToWavUrl(r.base64audio) : undefined),
  }));
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TranslationResponse,
  useTextTranslation,
} from "@/hooks/api/textTranslate";

import { Button } from "@/components/ui/button";
import { KeyHelper } from "@/components/common/key-helper";
import { MicButtonComponent } from "@/components/pages/voicechat/mic-button";
import { MinimalistAudioPlayer } from "@/components/pages/voicechat/minimalist-audio-player";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import useAudioTranscription from "@/hooks/webspeechTranscribe";
import { usePocketBase } from "@/context/pocketbase-context";
import { useTranslation } from "react-i18next";

interface TranslationPanelProps {
  session: string;
  sourceLang: Language;
  targetLang: Language;
  setIsPanelOpen: (isOpen: boolean) => void;
  isUserSpeaking: boolean;
  audioReadyCounter: number;
  newMessage?: VoiceChatMessage;
}

export function TranslationPanel({
  session,
  sourceLang,
  targetLang,
  setIsPanelOpen,
  isUserSpeaking,
  audioReadyCounter,
  newMessage,
}: TranslationPanelProps) {
  const { token } = usePocketBase();
  const { t } = useTranslation(["pages", "common"]);
  const { translateText, isTranslationLoading, translationError } =
    useTextTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isAudioDetected, setIsAudioDetected] = useState(isUserSpeaking);
  const [inputText, setInputText] = useState("");
  const [translationResponse, setTranslationResponse] = useState<
    TranslationResponse[]
  >([]);
  const [translateAudioPlayed, setTranslateAudioPlayed] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);

  const [sourceLanguage, setSourceLanguage] = useState(sourceLang);
  const [targetLanguage, setTargetLanguage] = useState(targetLang);
  const [isVoiceInput, setIsVoiceInput] = useState(false);
  const prevIsUserSpeakingRef = useRef(false);
  const prevAudioReadyCounterRef = useRef(audioReadyCounter);

  const { transcript, interimTranscript, resetTranscript } =
    useAudioTranscription({
      continuous: true,
      language: sourceLanguage.key,
      interimResults: true,
      maxAlternatives: 1,
      autoStart: isExpanded,
    });

  useEffect(() => {
    setSourceLanguage(sourceLang);
    setTargetLanguage(targetLang);
  }, [sourceLang, targetLang]);

  // Get selected text when button is clicked
  const getSelectedText = () => {
    const selection = window.getSelection();
    console.log("Selection:", selection);
    if (selection && selection.toString()) {
      // setSelectedText(selection.toString());
      setInputText(selection.toString());
      if (isInitialLoad) {
        // On initial load, if user selectes text its from target to source
        setSourceLanguage(targetLang);
        setTargetLanguage(sourceLang);
        setIsInitialLoad(false);
      }
    } else if (isInitialLoad) {
      // On initial load, if no text is selected, its from source to target
      setSourceLanguage(sourceLang);
      setTargetLanguage(targetLang);
      setIsInitialLoad(false);
    }
    setIsExpanded(!isExpanded);
  };

  // Swap languages
  const swapLanguages = () => {
    setSourceLanguage(targetLanguage);
    setTargetLanguage(sourceLanguage);
  };

  // To update mic button state (and color) when user is speaking
  useEffect(() => {
    setIsAudioDetected(isUserSpeaking);
  }, [isUserSpeaking]);

  // Speech start: clear textarea and enter voice input mode
  useEffect(() => {
    if (isUserSpeaking && !prevIsUserSpeakingRef.current && isExpanded) {
      setIsVoiceInput(true);
      setInputText("");
      setTranslationResponse([]);
      resetTranscript();
    }
    prevIsUserSpeakingRef.current = isUserSpeaking;
  }, [isUserSpeaking, isExpanded, resetTranscript]);

  // Sync transcript to textarea during voice input
  useEffect(() => {
    if (!isExpanded || !isVoiceInput) return;
    const displayText =
      transcript + (interimTranscript ? " " + interimTranscript : "");
    if (displayText.trim()) {
      setInputText(displayText.trim());
    }
  }, [transcript, interimTranscript, isExpanded, isVoiceInput]);

  // Trigger REST translation when speech segment completes
  useEffect(() => {
    if (prevAudioReadyCounterRef.current === audioReadyCounter) return;
    prevAudioReadyCounterRef.current = audioReadyCounter;

    if (!isExpanded || !isVoiceInput) return;

    const textToTranslate = transcript.trim();
    if (!textToTranslate) return;

    setInputText(textToTranslate);
    setIsVoiceInput(false);
    setTranslationResponse([]);

    void translateText(
      session,
      token,
      textToTranslate,
      sourceLanguage,
      targetLanguage,
      targetLanguage.key === targetLang.key,
    ).then((r) => setTranslationResponse(normalizeResponses(r)));

    resetTranscript();
  }, [audioReadyCounter]);

  // Cleanup on panel close
  useEffect(() => {
    if (!isExpanded) {
      resetTranscript();
      setIsVoiceInput(false);
    }
  }, [isExpanded, resetTranscript]);

  // Reset audio autoplay state when new translation response is expected
  useEffect(() => {
    console.log("Translation response: ", translationResponse);
    if (translationResponse.length == 0) {
      setTranslateAudioPlayed(false);
    }
  }, [translationResponse]);

  // Handle websocket input message (skipped when using client-side transcription)
  useEffect(() => {
    if (isVoiceInput) return;
    console.log("TranslationPanel newMessage: ", newMessage);
    if (newMessage?.message && newMessage.role === Role.User) {
      setInputText(newMessage.message);
      setTranslationResponse([]);
      setIsLoading(true);
      return;
    }
    if (newMessage?.audioSrc && newMessage.role === Role.Assistant) {
      setTranslationResponse((prev) => [
        ...prev,
        {
          text: newMessage.message || "",
          src: newMessage.audioSrc,
        },
      ]);
      setIsLoading(false);
    }
  }, [newMessage, isVoiceInput]);

  // Handle click outside to close panel
  useEffect(() => {
    setIsPanelOpen(isExpanded);
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        // Validate if click is outside of select elements
        !(
          document.getElementById("source-language-select") &&
          document
            .getElementById("source-language-select")
            ?.contains(event.target as Node)
        ) &&
        !(
          document.getElementById("target-language-select") &&
          document
            .getElementById("target-language-select")
            ?.contains(event.target as Node)
        )
      ) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  // Add keyboard event listeners
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Enter") {
        event.preventDefault();
        if (!isExpanded) {
          // First enter to open panel
          getSelectedText();
          setIsExpanded(true);
        } else if (isExpanded && inputText == "") {
          // Enter again without any text
          setIsExpanded(false);
        } else if (isExpanded && inputText != "") {
          // Enter with text input trigger translation
          void handleSubmit(event);
        }
      } else if (event.code === "Escape") {
        setIsExpanded(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded, inputText]);

  const handleSubmit = async (event?: Event) => {
    if (!inputText.trim()) {
      return;
    }

    event?.preventDefault();
    setTranslationResponse([]);
    const translations = await translateText(
      session,
      token,
      inputText,
      sourceLanguage,
      targetLanguage,
      targetLanguage.key === targetLang.key ? true : false,
    );
    setTranslationResponse(normalizeResponses(translations));
  };

  useEffect(() => {
    setIsLoading(isTranslationLoading);
    setError(translationError);
  }, [isTranslationLoading, translationError]);

  return (
    <div
      className="fixed right-5 md:right-8 bottom-8 md:bottom-20 z-20"
      ref={panelRef}
    >
      <div className="relative">
        <Button
          onClick={getSelectedText}
          className="w-12 h-12 md:w-12 md:h-12 p-3 rounded-md transition-all duration-300 ease-in-out bg-blue-600 hover:bg-blue-800"
          data-tour="translation-toggle"
        >
          <LanguagesIcon className="p-0 w-10 h-10 md:w-10 md:h-10" />
        </Button>
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 text-xs text-foreground/80 flex items-center space-x-1">
          <KeyHelper keyName={t("pages:translationPanel.enterKey")} />
        </div>
      </div>
      {isExpanded && (
        <Card className="p-2 absolute right-0 bottom-full mb-2 w-[350px] sm:w-[400px] shadow-lg border border-blue-800 border-2">
          <div className="flex justify-between items-center border-b">
            <h3 className="font-medium">
              {t("pages:translationPanel.header")}
            </h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsExpanded(false)}
              aria-label="Close translation panel"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardContent className="p-2">
            <div className="grid gap-4">
              <div className="flex items-center gap-2">
                <Select
                  value={sourceLanguage.key}
                  onValueChange={(key) => {
                    const lang = getLanguageByKey(key);
                    setSourceLanguage(lang);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Source language" />
                  </SelectTrigger>
                  <SelectContent id="source-language-select">
                    {SupportedLanguages.map((lang) => (
                      <SelectItem key={lang.key} value={lang.key}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button variant="ghost" size="icon" onClick={swapLanguages}>
                  <ArrowRightLeft className="h-4 w-4" />
                </Button>

                <Select
                  value={targetLanguage.key}
                  onValueChange={(key) => {
                    const lang = getLanguageByKey(key);
                    setTargetLanguage(lang);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Target language" />
                  </SelectTrigger>
                  <SelectContent id="target-language-select">
                    {SupportedLanguages.map((lang) => (
                      <SelectItem key={lang.key} value={lang.key}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4">
                <Textarea
                  placeholder={t("pages:translationPanel.inputPlaceholder")}
                  value={inputText}
                  onChange={(e) => {
                    setIsVoiceInput(false);
                    setInputText(e.target.value);
                  }}
                  className="min-h-[80px]"
                  autoFocus
                />
                <MicButtonComponent
                  isMuted={false}
                  audioDetected={isAudioDetected}
                  disabled={false}
                  onClick={() => {}}
                  size="sm"
                />
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button
                onClick={() => void handleSubmit()}
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-800"
              >
                {isLoading ? (
                  <>
                    <Spinner className="size-4" />
                    {t("pages:translationPanel.translating")}
                  </>
                ) : (
                  t("pages:translationPanel.translate")
                )}
              </Button>
              {translationResponse && translationResponse.length > 0 && (
                <div className="p-3 bg-muted rounded-md max-h-60 overflow-auto">
                  {/* <p className="text-sm font-medium">
                    {t("pages:translationPanel.translation")}
                  </p> */}
                  {translationResponse.map((response, index) => (
                    <div
                      key={index}
                      className="flex items-center p-1 border-b last:border-none"
                    >
                      {response.src && (
                        <MinimalistAudioPlayer
                          src={response.src}
                          size="sm"
                          autoPlay={
                            !translateAudioPlayed && index == 0 ? true : false
                          }
                          audioPlayed={setTranslateAudioPlayed}
                        />
                      )}
                      <div className="ml-4 text-sm">
                        <p>{response.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
